import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Paper, Typography, TextField, Button, Chip, IconButton, Switch, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem,
  ListItemText, Tooltip, MenuItem, Alert,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, ArrowUpward as UpIcon, ArrowDownward as DownIcon } from '@mui/icons-material';
import { ratingAPI } from '../../services/api';
import { errorText } from '../../utils/format';
import { StarRow, isStars } from './shared';

function Preview({ criteria, scaleType }) {
  const stars = isStars(scaleType);
  return (
    <Paper variant="outlined" sx={{ borderRadius: 4, p: 2, bgcolor: '#f5f7fa', maxWidth: 360, mx: 'auto' }}>
      <Typography variant="caption" color="text.secondary">Student app preview</Typography>
      <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.5 }}>Rate your faculty</Typography>
      {criteria.filter((c) => c.active).map((c) => (
        <Box key={c.id} sx={{ mb: 1.5 }}>
          <Typography variant="body2" fontWeight={700}>{c.name}</Typography>
          {c.description && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{c.description}</Typography>}
          {stars ? <StarRow value={4} size={24} /> : (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                <Box key={n} sx={{ width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700,
                  bgcolor: n === 8 ? '#00796b' : '#fff', color: n === 8 ? '#fff' : '#546e7a', border: '1px solid #b0bec5' }}>{n}</Box>
              ))}
            </Box>
          )}
        </Box>
      ))}
      {criteria.filter((c) => c.active).length === 0 && <Typography variant="body2" color="error">No active questions. Students cannot give feedback until you switch one on.</Typography>}
    </Paper>
  );
}

export default function QuestionsTab({ config, notify, onChanged }) {
  const scaleType = config?.scaleType || 'STARS_5';
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', weight: 1 });
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    try { setItems((await ratingAPI.getCriteria()).data); }
    catch (e) { notify(errorText(e, 'Could not load the questions'), 'error'); }
  }, [notify]);
  useEffect(() => { load(); }, [load]);

  const openDialog = (c = null) => { setEditing(c); setForm(c ? { name: c.name, description: c.description || '', weight: c.weight } : { name: '', description: '', weight: 1 }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await ratingAPI.updateCriterion(editing.id, form); else await ratingAPI.createCriterion(form);
      notify(editing ? 'Question updated' : 'Question added');
      setOpen(false);
      load();
      onChanged();
    } catch (e) { notify(errorText(e, 'Could not save'), 'error'); }
  };

  const toggle = async (c, active) => {
    try { await ratingAPI.updateCriterion(c.id, { active }); load(); onChanged(); }
    catch (e) { notify(errorText(e, 'Could not update'), 'error'); }
  };

  const move = async (index, dir) => {
    const next = [...items];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    setItems(next);
    try { await ratingAPI.reorderCriteria(next.map((c) => c.id)); onChanged(); }
    catch (e) { notify(errorText(e, 'Could not save the order'), 'error'); load(); }
  };

  const remove = async () => {
    try { await ratingAPI.deleteCriterion(confirm.id); notify('Question deleted'); setConfirm(null); load(); onChanged(); }
    catch (e) { notify(errorText(e, 'Could not delete'), 'error'); setConfirm(null); }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={7}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>Feedback questions</Typography>
            <Typography variant="caption" color="text.secondary">Students score every active question for each faculty member.</Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}>Add question</Button>
        </Box>
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <List disablePadding>
            {items.map((c, i) => (
              <ListItem key={c.id} divider sx={{ opacity: c.active ? 1 : 0.55, alignItems: 'flex-start' }}
                secondaryAction={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title="Move up"><span><IconButton size="small" disabled={i === 0} onClick={() => move(i, -1)}><UpIcon fontSize="small" /></IconButton></span></Tooltip>
                    <Tooltip title="Move down"><span><IconButton size="small" disabled={i === items.length - 1} onClick={() => move(i, 1)}><DownIcon fontSize="small" /></IconButton></span></Tooltip>
                    <Tooltip title={c.active ? 'Shown to students' : 'Hidden from students'}><Switch size="small" checked={c.active} onChange={(e) => toggle(c, e.target.checked)} /></Tooltip>
                    <IconButton size="small" onClick={() => openDialog(c)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => setConfirm(c)}><DeleteIcon fontSize="small" /></IconButton>
                  </Box>
                }>
                <ListItemText sx={{ pr: 20 }}
                  primary={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography fontWeight={700}>{i + 1}. {c.name}</Typography>
                    {c.weight > 1 && <Chip size="small" label={`Weight ${c.weight}x`} color="primary" variant="outlined" />}
                    {!c.active && <Chip size="small" label="Hidden" />}
                  </Box>}
                  secondary={<>{c.description || 'No description'}<Typography component="span" variant="caption" sx={{ display: 'block' }}>{c.usage ? `${c.usage} score(s) recorded` : 'Not used yet'}</Typography></>} />
              </ListItem>
            ))}
            {items.length === 0 && <ListItem><ListItemText primary="No questions yet" secondary="Add at least one so students can give feedback." /></ListItem>}
          </List>
        </Paper>
        <Alert severity="info" sx={{ mt: 2 }}>
          A question that already has scores cannot be deleted; switch it off instead so past feedback stays intact. A question with weight 2x counts twice as much in the overall score.
        </Alert>
      </Grid>
      <Grid item xs={12} md={5}>
        <Preview criteria={items} scaleType={scaleType} />
      </Grid>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#004d40', color: 'white' }}>{editing ? 'Edit question' : 'Add a feedback question'}</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField fullWidth size="small" label="Question / feature to score" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} sx={{ mt: 1, mb: 2 }} placeholder="e.g. Clinical demonstration" />
          <TextField fullWidth size="small" multiline rows={2} label="Help text shown to students (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} sx={{ mb: 2 }} />
          <TextField select fullWidth size="small" label="Importance in the overall score" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })}>
            {[1, 2, 3, 4, 5].map((w) => <MenuItem key={w} value={w}>{w === 1 ? 'Normal (1x)' : `${w}x`}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}><Button onClick={() => setOpen(false)}>Cancel</Button><Button variant="contained" onClick={save}>{editing ? 'Update' : 'Add'}</Button></DialogActions>
      </Dialog>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete this question?</DialogTitle>
        <DialogContent><Typography>"{confirm?.name}" will be removed. If it already has scores, use the switch to hide it instead.</Typography></DialogContent>
        <DialogActions sx={{ p: 2.5 }}><Button onClick={() => setConfirm(null)}>Cancel</Button><Button color="error" variant="contained" onClick={remove}>Delete</Button></DialogActions>
      </Dialog>
    </Grid>
  );
}
